import { ProjectData } from '@/types/project';

export interface ProjectMetrics {
  // Existing - kept for backward compatibility
  totalRevenue: number;
  totalCosts: number;
  profit: number;
  margin: number;
  // New metrics
  totalContractValue: number;
  recognizedRevenue: number;
  projectedFinalValue: number;
  budgetVariance: number;
}

export function calculateMetrics(data: ProjectData): ProjectMetrics {
  // Ensure data exists
  if (!data?.value || !data?.costs) {
    return {
      totalRevenue: 0,
      totalContractValue: 0,
      recognizedRevenue: 0,
      projectedFinalValue: 0,
      totalCosts: 0,
      profit: 0,
      margin: 0,
      budgetVariance: 0,
    };
  }

  // Contract value (same as existing totalRevenue)
  const totalContractValue =
    (data.value.contractValue || 0) +
    (data.value.approvedVariations || 0) +
    (data.value.unapprovedVariations || 0);

  // Recognized revenue (invoiced minus retention)
  const retentionPercent = (data.value.retention || 0) / 100;
  const retentionAmount = (data.value.invoicedToDate || 0) * retentionPercent;
  const recognizedRevenue = (data.value.invoicedToDate || 0) - retentionAmount;

  // Projected final value
  const projectedFinalValue =
    totalContractValue + (data.value.forecastedRevenue || 0);

  // Total costs (unchanged)
  const costComponents = ['labour', 'materials', 'plant', 'subcontractor', 'overheads', 'staff'] as const;
  const totalCosts = costComponents.reduce((sum, key) =>
    sum + (data.costs[key]?.toDate || 0) + (data.costs[key]?.forecast || 0), 0
  );

  const profit = projectedFinalValue - totalCosts;
  const margin = projectedFinalValue !== 0 ? (profit / projectedFinalValue) * 100 : 0;
  const budgetVariance = (data.value.budgetedRevenue || 0) - projectedFinalValue;

  return {
    totalRevenue: totalContractValue, // KEEP for backward compatibility
    totalContractValue,
    recognizedRevenue,
    projectedFinalValue,
    totalCosts,
    profit,
    margin,
    budgetVariance,
  };
}